import {
  BooleanParameter,
  DEFAULT_VIEW_PARAMETERS,
  DEFAULT_WHEEL_PARAMETERS,
  NumberParameter,
} from './parameters';
import { Spoke } from './spoke';
import { getState } from './state';
import { presets } from './presets';

const toggleRealisticScaleElements = (realisticScaleValue: boolean) => {
  document.querySelectorAll('.realistic-only').forEach((el) => {
    el.classList.toggle('hidden', !realisticScaleValue);
  });
};

export const populateInputs = () => {
  const state = getState();
  const wheelParams = state.wheelParameters;
  for (const paramName in wheelParams) {
    const param = wheelParams[paramName as keyof typeof wheelParams];
    const input = document.querySelector(`input[name=${paramName}]`) as HTMLInputElement;
    if (!input && paramName !== 'spokes') {
      console.warn(`Input with name '${paramName}' not found!}'`);
      continue;
    }
    if (param instanceof NumberParameter) {
      input.value = (param as NumberParameter).format();
    } else if (param instanceof BooleanParameter) {
      input.checked = (param as BooleanParameter).value;
    }
  }
  const viewParams = state.viewParameters;
  for (const paramName in viewParams) {
    const param = viewParams[paramName as keyof typeof viewParams];
    const input = document.querySelector(`input[name=${paramName}]`) as HTMLInputElement;
    if (param instanceof NumberParameter) {
      input.value = (param as NumberParameter).format();
    } else if (param instanceof BooleanParameter) {
      input.checked = (param as BooleanParameter).value;
      if (paramName === 'realisticScale') {
        toggleRealisticScaleElements(param.value);
      }
    }
  }
};

export const readInput = (name: string) => {
  const state = getState();
  let input = document.querySelector(`input[name="${name}"]`);
  if (!input) {
    console.warn(`Input with name '${name}' not found!}'`);
    return;
  }
  // @ts-ignore
  const param = state.wheelParameters[name] ?? state.viewParameters[name];
  if (!param) {
    name !== 'spokes' && console.warn(`Parameter with name '${name}' not found!}'`);
    return;
  }
  if ((input as HTMLInputElement).type === 'number') {
    param.parse((input as HTMLInputElement).value);
  } else if ((input as HTMLInputElement).type === 'checkbox') {
    param.parse((input as HTMLInputElement).checked);
  }
  if (name === 'realisticScale') {
    toggleRealisticScaleElements(param.value);
  }
};

export const bindInputs = () => {
  document.querySelectorAll('input').forEach((input) => {
    if (Object.keys(DEFAULT_WHEEL_PARAMETERS).includes(input.name)) {
      input.addEventListener('change', () => {
        readInput(input.name);
        getState().refresh(true);
      });
    } else if (Object.keys(DEFAULT_VIEW_PARAMETERS).includes(input.name)) {
      input.addEventListener('change', () => {
        readInput(input.name);
        getState().refresh();
      });
    }
  });
};

export const bindClearSpokesBtn = () => {
  document.getElementById('clear-spokes-btn')?.addEventListener('click', () => {
    const state = getState();
    state.wheel?.removeAllSpokes();
    state.refresh();
  });
};

export const bindKeyboardShortcuts = () => {
  document.addEventListener('keyup', (event) => {
    const editingInputs = document.activeElement?.tagName.toLowerCase() === 'input';
    if (editingInputs) return;
    if (event.key === 'd' || event.key === 'D' || event.key === 'Backspace') {
      const state = getState();
      if (!state.selectedId) return;
      const selectedPart = state.wheel?.find(state.selectedId);
      if (!selectedPart) return;
      if (selectedPart instanceof Spoke) {
        state.wheel?.removeSpoke(state.selectedId);
        state.refresh();
      }
    }
  });
};

export const bindSelectResetting = () => {
  document.querySelectorAll('.select-resetting').forEach((el) => {
    el.addEventListener('click', () => {
      getState().select(undefined);
    });
  });
};

export const bindCopyUrlButton = () => {
  document.getElementById('copy-url-btn')?.addEventListener('click', () => {
    const url = makeBuildUrl();
    navigator.clipboard.writeText(url);
    const btn = document.getElementById('copy-url-btn');
    if (btn) {
      const txtBackup = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => {
        btn.textContent = txtBackup;
      }, 1500);
    }
  });
};

export const readQueryString = (searchParameters?: URLSearchParams) => {
  const searchParams = searchParameters ?? new URLSearchParams(window.location.search);
  const state = getState();
  const paramPairs = Array.from(searchParams.entries());
  if (paramPairs.length > 0) {
    window.umami?.track('UrlWithParams', { numParams: paramPairs.length });
  }
  for (const [name, value] of paramPairs) {
    // @ts-ignore
    const param = state.wheelParameters[name] ?? state.viewParameters[name];
    if (!param) {
      name !== 'spokes' && console.warn(`Parameter with name '${name}' not found!'`);
      continue;
    }
    param.parse(value);
    searchParams.delete(name);
  }
  const spokeDefs = searchParams.get('spokes');
  // Clean query parameters
  const url = new URL(window.location.href);
  Array.from(url.searchParams.keys()).forEach((key) => {
    url.searchParams.delete(key);
  });
  window.history.replaceState({}, '', url);
  return spokeDefs;
};

export const makeBuildUrl = (): string => {
  const url = new URL(window.location.href);
  Array.from(url.searchParams.keys()).forEach((key) => {
    url.searchParams.delete(key);
  });
  const state = getState();
  const allParams = { ...state.wheelParameters, ...state.viewParameters };
  for (const [paramName, param] of Object.entries(allParams)) {
    if (param.value !== param.defaultValue) {
      url.searchParams.set(paramName, param.format());
    }
  }
  if (state.wheel?.spokeDefs) {
    url.searchParams.set('spokes', state.wheel.spokeDefs);
  }
  return url.toString();
};

export const bindPresetSelect = () => {
  document.querySelector('select[name=preset-select]')?.addEventListener('change', (event) => {
    const select = event.target as HTMLSelectElement;
    const presetName = select.value;
    if (presetName === 'none') return;
    const presetUrl = presets[presetName];
    if (!presetUrl) return;
    const spokeDefs = readQueryString(new URLSearchParams(presetUrl));
    populateInputs();
    getState().refresh(true);
    !!spokeDefs && getState().wheel?.generateSpokesFromDefs(spokeDefs);
    getState().refresh(true);
    select.value = 'none';
  });
};

export const bindHelpBtn = () => {
  document.getElementById('help-btn')?.addEventListener('click', (event) => {
    event.preventDefault();
    document.querySelector('.help-modal')?.classList.toggle('hidden');
  });
  document.getElementById('help-close-btn')?.addEventListener('click', (event) => {
    event.preventDefault();
    console.log('closing...');
    document.querySelector('.help-modal')?.classList.toggle('hidden');
  });
};

export const bindSpinBtn = () => {
  let animationStart: number = -1;
  let prevAngle: number = 0;
  let origTransform: string | undefined = '';
  let originalBtnText: string = '';
  let animationId: number | undefined = undefined;

  const stopSpin = () => {
    const spinBtn = document.getElementById('spin-btn');
    if (animationId) {
      cancelAnimationFrame(animationId!);
    }
    animationStart = -1;
    prevAngle = 0;
    if (origTransform) {
      document.querySelector('.wheel')?.setAttribute('transform', origTransform);
    }
    if (spinBtn) {
      spinBtn.textContent = originalBtnText;
    }
    originalBtnText = '';
    animationId = undefined;
    document.querySelector('input[name="realisticScale"]')?.removeAttribute('disabled');
  };

  const animateSpin = (timestamp: number) => {
    const svgContainer = document.querySelector('svg.svg-container');
    const wheelElem = document.querySelector('.wheel');
    const spinBtn = document.getElementById('spin-btn')!;
    if (!svgContainer || !wheelElem) return;
    const w = Number.parseFloat(svgContainer.getAttribute('width') || '0');
    const h = Number.parseFloat(svgContainer.getAttribute('height') || '0');
    if (animationStart < 0) {
      originalBtnText = spinBtn.textContent;
      spinBtn.textContent = 'Stop';
      animationStart = timestamp;
      origTransform = wheelElem.getAttribute('transform') || '';
      document.querySelector('input[name="realisticScale"]')?.setAttribute('disabled', 'true');
    }
    const elapsedSec = (timestamp - animationStart) / 1_000;
    const dirMult = getState().viewParameters.ndsView.value ? -1 : 1;
    const angleSpeed = Math.min(3 / elapsedSec, 3600) * dirMult;
    const angle = (prevAngle + angleSpeed) % 360;
    prevAngle = angle;
    const transform = `${origTransform} rotate(${angle} ${w / 2} ${h / 2})`;
    wheelElem.setAttribute('transform', transform);
    if (Math.abs(angleSpeed) > 0.35 || Math.abs(angle) > 5) {
      animationId = requestAnimationFrame(animateSpin);
    } else {
      stopSpin();
    }
  };

  document.getElementById('spin-btn')?.addEventListener('click', () => {
    const spinBtn = document.getElementById('spin-btn')!;
    if (spinBtn.textContent === 'Stop') {
      stopSpin();
    } else {
      const wheelElem = document.querySelector('.wheel');
      if (!wheelElem) return;
      animationId = requestAnimationFrame(animateSpin);
    }
  });
};

export const bindPrint = () => {
  window.addEventListener('beforeprint', () => {
    if (window.umami?.track) {
      window.umami.track('PrintPreviewOpened');
    }
  });
};
